'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'motion/react';

import { defaultProcessText } from 'app/utility/textProcessing';
import { IImage } from 'app/interfaces/common';
import EllipseBlur from 'app/components/common/EllipseBlur';
import usePreloaderMainStore from 'app/components/common/PreloaderMain/store';
import classNames from 'classnames';
import ImageBg from 'app/components/common/ImageBg';

export default function Welcome({
  images = [],
  thumbs = [],
  texts = [],
  description = ''
}: {
  images?: IImage[][];
  thumbs?: IImage[];
  texts?: string[];
  description?: string;
}) {
  const [textIndex, setTextIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref);
  const { isHidden: isPreloaderHidden } = usePreloaderMainStore();

  useEffect(() => {
    if (!isPreloaderHidden) return;

    let intervalId = 0;

    const startAnimationLoop = () => {
      clearInterval(intervalId);
      if (document.hidden) {
        return;
      }
      intervalId = window.setInterval(() => {
        if (!texts.length) {
          setImageIndex(prev => (prev + 1) % images.length);
        } else {
          setTextIndex(prev => (prev + 1) % texts.length);
        }
        setIsFirstRender(false);
      }, 2500);
    };

    document.removeEventListener('visibilitychange', startAnimationLoop);

    if (!isInView) return;

    document.addEventListener('visibilitychange', startAnimationLoop);

    startAnimationLoop();

    return () => {
      setIsFirstRender(true);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', startAnimationLoop);
    };
  }, [isInView, texts, images, isPreloaderHidden]);

  useEffect(() => {
    if (!texts.length) return;
    setImageIndex(Math.min(images.length - 1, textIndex % texts.length));
  }, [textIndex, texts, images]);

  return (
    <div
      className="h-screen max-h-[640px] md:max-h-[800px] 2xl:max-h-none relative bg-graphite overflow-hidden"
      ref={ref}
    >
      <AnimatePresence>
        {images.map((imageSet, index) => {
          return (
            index === imageIndex && (
              <motion.div
                key={index}
                initial={
                  index === 0 && isFirstRender ? { opacity: 1 } : { opacity: 0 }
                }
                animate={{ opacity: 1, scale: 1.05 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: 1.2 }}
                className="absolute inset-0"
              >
                {!!imageSet?.[0]?.url && (
                  <ImageBg
                    src={imageSet[0].url}
                    fill
                    alt={imageSet[0].alt || ''}
                    placeholderDataURL={imageSet[0].placeholder_data_url}
                    priority
                    className={classNames('object-cover', {
                      'portrait:hidden': !!imageSet[1]
                    })}
                  />
                )}

                {!!imageSet?.[1]?.url && (
                  <ImageBg
                    src={imageSet[1].url}
                    fill
                    alt={imageSet[1].alt || ''}
                    placeholderDataURL={imageSet[1].placeholder_data_url}
                    priority
                    className="object-cover landscape:hidden"
                  />
                )}
              </motion.div>
            )
          );
        })}
      </AnimatePresence>

      <div className="absolute w-[1380px] md:w-[1812px] 2xl:w-[125%] top-0 left-0 -translate-x-[50%] -translate-y-[50%]">
        <EllipseBlur />
      </div>

      <div className="w-full h-full absolute inset-0 grid grid-rows-[1fr,auto,1fr]">
        <div className="wrapper-main wrapper-max w-full md:grid md:grid-cols-12 md:gap-3 xl:gap-4 self-start pt-24 portrait:pt-40 md:pt-5 md:portrait:pt-5">
          {!!thumbs?.[0]?.url && (
            <div className="hidden md:block col-span-2 xl:col-span-1 col-start-3 xl:col-start-3">
              <div className="relative pb-[125%] md:pb-[144%] 2xl:pb-[160%] rounded overflow-hidden isolate">
                <ImageBg
                  src={thumbs[0].url}
                  fill
                  alt={thumbs[0].alt || ''}
                  placeholderDataURL={thumbs[0].placeholder_data_url}
                  className="object-cover rounded"
                  priority
                />
              </div>
            </div>
          )}
          {!!description && (
            <div className="col-span-5 sm:col-span-4">
              <div
                className="text-h1 text-white max-w-[320px]"
                dangerouslySetInnerHTML={{
                  __html: defaultProcessText(description)
                }}
              ></div>
            </div>
          )}
        </div>

        <div className="py-8 w-full self-center">
          <div className="relative text-outlined text-2xl md:text-4xl xl:text-7xl">
            <div className="text-white text-center h-[1em] relative overflow-hidden">
              <AnimatePresence>
                {textIndex === 0 && (
                  <motion.div
                    key={0}
                    initial={{ opacity: 0, y: '100%' }}
                    animate={
                      isPreloaderHidden
                        ? { opacity: 1, y: '0%' }
                        : { opacity: 0, y: '100%' }
                    }
                    exit={{ opacity: 0, y: '-100%' }}
                    transition={{ duration: 1, ease: [0.77, 0, 0.58, 1] }}
                    dangerouslySetInnerHTML={{
                      __html: defaultProcessText(texts[0])
                    }}
                    className="absolute w-full h-full top-0 left-0"
                  ></motion.div>
                )}
                {textIndex > 0 && !!texts[1] && (
                  <motion.div
                    key={1}
                    initial={{ opacity: 0, y: '100%' }}
                    animate={{ opacity: 1, y: '0%' }}
                    exit={{ opacity: 0, y: '-100%' }}
                    transition={{ duration: 1, ease: [0.77, 0, 0.58, 1] }}
                    className="absolute w-full h-full top-0 left-0"
                    dangerouslySetInnerHTML={{
                      __html: defaultProcessText(texts[1])
                    }}
                  ></motion.div>
                )}
                {textIndex === 2 && !!texts[2] && (
                  <motion.div
                    key={2}
                    initial={{ opacity: 0, y: '100%' }}
                    animate={{ opacity: 1, y: '0%' }}
                    exit={{ opacity: 0, y: '-100%' }}
                    transition={{ duration: 1, ease: [0.77, 0, 0.58, 1] }}
                    dangerouslySetInnerHTML={{
                      __html: defaultProcessText(texts[2])
                    }}
                    className="absolute w-full h-full top-0 left-0"
                  ></motion.div>
                )}
              </AnimatePresence>
            </div>
            {!!thumbs?.length && thumbs.length > 1 && (
              <div className="absolute w-full top-full -mt-[0.2em]">
                <div className="wrapper-main wrapper-max grid grid-cols-6 md:grid-cols-12 gap-1.5 md:gap-3 xl:gap-4">
                  <AnimatePresence>
                    {textIndex === 2 && !!thumbs[1]?.url && (
                      <motion.div
                        key={0}
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: '0%' }}
                        exit={{ opacity: 0, y: '100%' }}
                        transition={{ duration: 1, ease: [0.77, 0, 0.58, 1] }}
                        className="col-span-1 portrait:col-span-2 md:col-span-2 xl:col-span-1 col-start-3 portrait:col-start-3 md:col-start-7 md:portrait:col-start-7 xl:col-start-8"
                      >
                        <div className="relative pb-[125%] md:pb-[144%] 2xl:pb-[160%] rounded overflow-hidden isolate">
                          <ImageBg
                            src={thumbs[1].url}
                            fill
                            alt={thumbs[1].alt || ''}
                            placeholderDataURL={thumbs[1].placeholder_data_url}
                            className="object-cover rounded"
                            priority
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {textIndex === 2 && !!thumbs[2]?.url && (
                      <motion.div
                        key={1}
                        initial={{ opacity: 0, y: '50%' }}
                        animate={{ opacity: 1, y: '0%' }}
                        exit={{ opacity: 0, y: '50%' }}
                        transition={{ duration: 1, ease: [0.77, 0, 0.58, 1] }}
                        className="col-span-1 portrait:col-span-2 md:col-span-2 xl:col-span-1 col-start-5 md:col-start-11 md:portrait:col-start-11 xl:col-start-11"
                      >
                        <div className="relative pb-[125%] md:pb-[144%] 2xl:pb-[160%] rounded overflow-hidden isolate">
                          <ImageBg
                            src={thumbs[2].url}
                            fill
                            alt={thumbs[2].alt || ''}
                            placeholderDataURL={thumbs[2].placeholder_data_url}
                            className="object-cover rounded"
                            priority
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="wrapper-main wrapper-max w-full md:grid md:grid-cols-12 md:gap-3 xl:gap-4 pb-8 self-end">
          <div className="text-h1 text-white col-span-2 lg:col-start-4">
            Листать
          </div>
        </div>
      </div>
    </div>
  );
}
